// Attendance periods register + roster generator.
import React, { useState } from 'react';
import { api, ApiError } from '../api';
import { RegisterPage, FieldSpec, useLookup, dateCol, statusCol } from '../crud';
import { Modal, Field, DateField, ErrorBox } from '../ui';
import { fullName, toAus, ATTENDANCE_STATUSES, ROSTER_PATTERNS } from '../util';

const ROSTERS = ROSTER_PATTERNS;

export default function Attendance() {
  const people = useLookup('people');
  const projects = useLookup('projects');
  const work_areas = useLookup('work_areas');
  const [showGenerator, setShowGenerator] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const specs: FieldSpec[] = [
    { name: 'person_id', label: 'Person', type: 'select', lookup: 'people', required: true, section: 'Who & where' },
    { name: 'project_id', label: 'Project', type: 'select', lookup: 'projects', required: true, default: projects.rows[0]?.id, section: 'Who & where' },
    { name: 'work_area_id', label: 'Work area', type: 'select', lookup: 'work_areas', section: 'Who & where' },
    { name: 'supervisor', label: 'Supervisor', type: 'text', section: 'Who & where' },
    { name: 'status', label: 'Status', type: 'select', options: ATTENDANCE_STATUSES, default: 'planned', section: 'Who & where' },
    { name: 'roster_type', label: 'Roster type', type: 'select', options: ROSTERS, default: 'One-off visit', section: 'Who & where' },
    { name: 'check_in', label: 'Planned check-in', type: 'date', required: true, section: 'Planned dates' },
    { name: 'check_out', label: 'Planned check-out', type: 'date', required: true, section: 'Planned dates' },
    { name: 'first_work', label: 'First work day', type: 'date', section: 'Planned dates' },
    { name: 'last_work', label: 'Last work day', type: 'date', section: 'Planned dates' },
    { name: 'actual_check_in', label: 'Actual check-in', type: 'date', section: 'Actual dates (leave blank until known)' },
    { name: 'actual_check_out', label: 'Actual check-out', type: 'date', section: 'Actual dates (leave blank until known)' },
    { name: 'actual_first_work', label: 'Actual first work day', type: 'date', section: 'Actual dates (leave blank until known)' },
    { name: 'actual_last_work', label: 'Actual last work day', type: 'date', section: 'Actual dates (leave blank until known)' },
    { name: 'accommodation_required', label: 'Accommodation required', type: 'bool', default: 1, section: 'Accommodation & meals' },
    { name: 'room_type', label: 'Room type required', type: 'text', section: 'Accommodation & meals' },
    { name: 'meals_required', label: 'Meals required', type: 'bool', default: 1, section: 'Accommodation & meals' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Accommodation & meals' }
  ];

  return (
    <>
      <RegisterPage
        key={reloadKey}
        title="Attendance periods"
        entity="attendance"
        specs={specs}
        lookupEntities={{ people, projects, work_areas }}
        extraToolbar={<button className="btn" onClick={() => setShowGenerator(true)}>⚙ Roster generator</button>}
        columns={[
          { key: 'person_id', label: 'Person', render: r => <strong>{people.byId[r.person_id] || r.person_id}</strong>, sortValue: r => people.byId[r.person_id] },
          statusCol(),
          dateCol('check_in', 'Check-in'),
          dateCol('check_out', 'Check-out'),
          { key: 'actuals', label: 'Actuals', render: r => r.actual_check_in || r.actual_check_out ? `${toAus(r.actual_check_in)} → ${toAus(r.actual_check_out)}` : '', sortValue: r => r.actual_check_in },
          { key: 'roster_type', label: 'Roster' },
          { key: 'work_area_id', label: 'Work area', render: r => work_areas.byId[r.work_area_id] || '', sortValue: r => work_areas.byId[r.work_area_id] },
          { key: 'accommodation_required', label: 'Accom.', render: r => r.accommodation_required ? 'Yes' : 'No' },
          { key: 'modified', label: 'Last modified', render: r => r.modified_by ? `${r.modified_by} ${r.modified_at?.slice(0, 10) || ''}` : (r.created_by || ''), sortValue: r => r.modified_at }
        ]}
      />
      {showGenerator && (
        <RosterGenerator
          people={people.options} projects={projects.options} areas={work_areas.options}
          onClose={() => setShowGenerator(false)}
          onSaved={() => { setShowGenerator(false); setReloadKey(k => k + 1); }} />
      )}
    </>
  );
}

function RosterGenerator({ people, projects, areas, onClose, onSaved }: {
  people: { value: any; label: string }[];
  projects: { value: any; label: string }[];
  areas: { value: any; label: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const [v, setV] = useState<any>({ pattern: '2 weeks on / 1 week off', months: 6, status: 'planned', project_id: projects[0]?.value, accommodation_required: true });
  const [preview, setPreview] = useState<any[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const gen = async (save: boolean) => {
    setBusy(true);
    setErrors([]);
    try {
      const r = await api('/roster/generate', { method: 'POST', body: { ...v, save } });
      if (save) onSaved();
      else setPreview(r.preview);
    } catch (e: any) {
      setErrors(e instanceof ApiError && e.errors.length ? e.errors : [e.message]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Roster generator" onClose={onClose} wide>
      <p className="muted">Generates future attendance periods from a roster pattern. You can edit or delete any generated period afterwards.</p>
      <ErrorBox error={errors} onDismiss={() => setErrors([])} />
      <div className="form-grid">
        <Field label="Person *">
          <select className="input" value={v.person_id ?? ''} onChange={e => setV({ ...v, person_id: Number(e.target.value) || undefined })}>
            <option value="">—</option>
            {people.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Project *">
          <select className="input" value={v.project_id ?? ''} onChange={e => setV({ ...v, project_id: Number(e.target.value) || undefined })}>
            {projects.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Work area">
          <select className="input" value={v.work_area_id ?? ''} onChange={e => setV({ ...v, work_area_id: Number(e.target.value) || undefined })}>
            <option value="">—</option>
            {areas.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Pattern">
          <select className="input" value={v.pattern} onChange={e => setV({ ...v, pattern: e.target.value })}>
            {ROSTERS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Start date *"><DateField value={v.start_date} onChange={x => setV({ ...v, start_date: x })} /></Field>
        <Field label="Months ahead">
          <input className="input" type="number" min={1} max={12} value={v.months}
            onChange={e => setV({ ...v, months: Number(e.target.value) })} />
        </Field>
        <Field label="Status">
          <select className="input" value={v.status} onChange={e => setV({ ...v, status: e.target.value })}>
            <option value="planned">planned</option>
            <option value="tentative">tentative</option>
            <option value="confirmed">confirmed</option>
          </select>
        </Field>
        <Field label="Accommodation required">
          <input type="checkbox" className="checkbox" checked={!!v.accommodation_required}
            onChange={e => setV({ ...v, accommodation_required: e.target.checked })} />
        </Field>
      </div>
      <div className="btn-row form-actions">
        <button className="btn" disabled={busy || !v.start_date} onClick={() => gen(false)}>Preview</button>
        <button className="btn btn-primary" disabled={busy || !v.start_date || !v.person_id || !v.project_id} onClick={() => gen(true)}>Generate &amp; save</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
      {preview && (
        <div style={{ marginTop: 14 }}>
          <h4>Preview — {preview.length} period(s)</h4>
          <table>
            <thead><tr><th>#</th><th>Check-in</th><th>Check-out</th><th>First work</th><th>Last work</th></tr></thead>
            <tbody>
              {preview.map((p, i) => (
                <tr key={i}><td>{i + 1}</td><td>{toAus(p.check_in)}</td><td>{toAus(p.check_out)}</td><td>{toAus(p.first_work)}</td><td>{toAus(p.last_work)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
